import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAPI } from '../../state';
import { ORG_CONFIG_STEPS, OrgConfigStep } from './constants';
import { useCurrentVersionAPI } from './selectors';
var ORG_CONFIG_VERSION_ENSURE_API = '/api/orgConfig/version/ensureWorking';
export var OrgConfigWizardContext = createContext({});
var REVIEW_STEP_INDEX = ORG_CONFIG_STEPS.indexOf(OrgConfigStep.Review);
export var useOrgConfigWizardParams = function (_a) {
  var _b;
  var _c = _a === void 0 ? {} : _a,
    _d = _c.isViewOnly,
    isViewOnly = _d === void 0 ? false : _d;
  var _e = useSearchParams(),
    searchParams = _e[0],
    setSearchParams = _e[1];
  var urlStep = searchParams.get('step');
  var parsedStep = urlStep ? Number.parseInt(urlStep, 10) : 0;
  var rawStepIndex = Math.max(0, Math.min(Number.isNaN(parsedStep) ? 0 : parsedStep, ORG_CONFIG_STEPS.length - 1));
  var stepIndex = isViewOnly ? REVIEW_STEP_INDEX : rawStepIndex;
  var step = (_b = ORG_CONFIG_STEPS[stepIndex]) !== null && _b !== void 0 ? _b : ORG_CONFIG_STEPS[0];
  var _f = useState(null),
    configVersionId = _f[0],
    setConfigVersionId = _f[1];
  var _g = useState(false),
    multiGeoEnabled = _g[0],
    setMultiGeoEnabled = _g[1];
  var _h = useState(null),
    selectedLocationId = _h[0],
    setSelectedLocationId = _h[1];
  var _j = useState(false),
    isBpo = _j[0],
    setIsBpo = _j[1];
  var _k = useState(false),
    isPublished = _k[0],
    setIsPublished = _k[1];
  var _l = useState(false),
    isReadOnly = _l[0],
    setIsReadOnly = _l[1];
  var _m = useState(false),
    hasUnpublishedChanges = _m[0],
    setHasUnpublishedChanges = _m[1];
  var _o = useState(null),
    beforeNextFn = _o[0],
    setBeforeNextFn = _o[1];
  var checkIsDirtyRef = useRef(null);
  var hasTouchedSessionRef = useRef(false);
  var allowExit = useRef(false);
  var _p = useState(false),
    isSaving = _p[0],
    setIsSaving = _p[1];
  var markSessionDirty = useCallback(function () {
    hasTouchedSessionRef.current = true;
  }, []);
  var setBeforeNext = useCallback(function (fn) {
    setBeforeNextFn(function () {
      return fn;
    });
  }, []);
  var setCheckIsDirty = useCallback(function (fn) {
    checkIsDirtyRef.current = fn;
  }, []);
  var _q = useAPI(ORG_CONFIG_VERSION_ENSURE_API, {}, 'POST'),
    ensureVersionData = _q[0],
    ensureWorkingVersionRequest = _q[1];
  var _r = useCurrentVersionAPI(),
    currentVersionData = _r[0],
    fetchCurrentVersion = _r[1],
    clearVersionData = _r[2];
  var versionData = isViewOnly ? currentVersionData : ensureVersionData;
  var ensureWorkingVersion = useCallback(function () {
    if (isViewOnly) fetchCurrentVersion();else ensureWorkingVersionRequest();
  }, [isViewOnly, fetchCurrentVersion, ensureWorkingVersionRequest]);
  useEffect(function () {
    ensureWorkingVersion();
  }, [ensureWorkingVersion]);
  useEffect(function () {
    if (versionData.data) {
      setConfigVersionId(versionData.data.configVersionId);
      setMultiGeoEnabled(versionData.data.multiGeoEnabled);
      setIsBpo(versionData.data.isBpo);
      setIsPublished(versionData.data.isPublished);
      setIsReadOnly(isViewOnly ? true : versionData.data.isReadOnly);
      setHasUnpublishedChanges(versionData.data.hasUnpublishedChanges);
    }
    return function () {
      clearVersionData();
    };
  }, [versionData.data, isViewOnly, clearVersionData]);
  var goToStep = useCallback(function (nextIndex) {
    if (isViewOnly) return;
    var newParams = new URLSearchParams(searchParams);
    newParams.set('step', String(Math.max(0, Math.min(nextIndex, ORG_CONFIG_STEPS.length - 1))));
    setSearchParams(newParams, {
      replace: true
    });
    globalThis.scrollTo(0, 0);
  }, [isViewOnly, searchParams, setSearchParams]);
  var value = useMemo(function () {
    var _a;
    return {
      step: step,
      stepIndex: stepIndex,
      goToStep: goToStep,
      configVersionId: configVersionId,
      multiGeoEnabled: multiGeoEnabled,
      isBpo: isBpo,
      isPublished: isPublished,
      isReadOnly: isReadOnly,
      isViewOnly: isViewOnly,
      ensureWorkingVersion: ensureWorkingVersion,
      versionLoading: (_a = versionData.requesting) !== null && _a !== void 0 ? _a : false,
      versionError: versionData.error,
      selectedLocationId: selectedLocationId,
      setSelectedLocationId: setSelectedLocationId,
      setBeforeNext: setBeforeNext,
      beforeNext: beforeNextFn,
      checkIsDirty: checkIsDirtyRef,
      setCheckIsDirty: setCheckIsDirty,
      hasTouchedSession: hasTouchedSessionRef,
      markSessionDirty: markSessionDirty,
      allowExit: allowExit,
      isSaving: isSaving,
      setIsSaving: setIsSaving,
      hasUnpublishedChanges: hasUnpublishedChanges
    };
  }, [step, stepIndex, goToStep, configVersionId, multiGeoEnabled, isBpo, isPublished, isReadOnly, isViewOnly, ensureWorkingVersion, versionData.requesting, versionData.error, selectedLocationId, setBeforeNext, beforeNextFn, checkIsDirtyRef, setCheckIsDirty, hasTouchedSessionRef, markSessionDirty, allowExit, isSaving, hasUnpublishedChanges]);
  return value;
};
export var useOrgConfigDirtyRegister = function (checkIsDirty) {
  var _a = useContext(OrgConfigWizardContext),
    setCheckIsDirty = _a.setCheckIsDirty,
    markSessionDirty = _a.markSessionDirty;
  var checkIsDirtyRef = useRef(checkIsDirty);
  checkIsDirtyRef.current = checkIsDirty;
  useEffect(function () {
    setCheckIsDirty === null || setCheckIsDirty === void 0 ? void 0 : setCheckIsDirty(checkIsDirty);
  }, [setCheckIsDirty, checkIsDirty]);
  useEffect(function () {
    return function () {
      var _a;
      if ((_a = checkIsDirtyRef.current) === null || _a === void 0 ? void 0 : _a.call(checkIsDirtyRef)) {
        markSessionDirty === null || markSessionDirty === void 0 ? void 0 : markSessionDirty();
      }
      setCheckIsDirty === null || setCheckIsDirty === void 0 ? void 0 : setCheckIsDirty(null);
    };
  }, [setCheckIsDirty, markSessionDirty]);
};